/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Match Booster common questions", description = "Answers to common questions about Match Booster identifier formats, enrichment timing, data storage, and match rate behavior.", lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Match Booster common questions",
        description: "Answers to common questions about Match Booster identifier formats, enrichment timing, data storage, and match rate behavior.",
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "What format should source identifiers use?",
          "depth": 1,
          "id": "what-format-should-source-identifiers-use"
        }, {
          "value": "How should I model multiple identifiers of the same type?",
          "depth": 1,
          "id": "how-should-i-model-multiple-identifiers-of-the-same-type"
        }, {
          "value": "When does enrichment take place? Does Hightouch store a copy of my data?",
          "depth": 1,
          "id": "when-does-enrichment-take-place-does-hightouch-store-a-copy-of-my-data"
        }, {
          "value": "How long does model enrichment take?",
          "depth": 1,
          "id": "how-long-does-model-enrichment-take"
        }, {
          "value": "How often do enriched models refresh?",
          "depth": 1,
          "id": "how-often-do-enriched-models-refresh"
        }, {
          "value": "How can I check when my model was last enriched?",
          "depth": 1,
          "id": "how-can-i-check-when-my-model-was-last-enriched"
        }, {
          "value": "Can I manually trigger an enrichment?",
          "depth": 1,
          "id": "can-i-manually-trigger-an-enrichment"
        }, {
          "value": "Why do my match rates appear lower with Match Booster?",
          "depth": 1,
          "id": "why-do-my-match-rates-appear-lower-with-match-booster"
        }, {
          "value": "Why is Snapchat reporting greater than 100% match?",
          "depth": 1,
          "id": "why-is-snapchat-reporting-greater-than-100-match"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      hr: "hr",
      h3: "h3",
      a: "a",
      code: "code",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams, marketers"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Answers to frequently asked questions about identifier formats, enrichment timing, data handling, and match rate behavior."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "what-format-should-source-identifiers-use",
        children: "What format should source identifiers use?"
      }), "\n", _jsx(_components.p, {
        children: "Match Booster supports hashed (SHA256 or MD5) identifiers for emails and phone numbers. Hashed values should be lowercased and trimmed before hashing."
      }), "\n", _jsxs(_components.p, {
        children: ["For US enrichment, you can also provide name and address fields as SHA256 hashes. These follow a stricter hashing recipe. See ", _jsx(_components.a, {
          href: "/match-booster/configure-model#hashed-names-and-addresses",
          children: "Hashed names and addresses"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Other PII identifiers (plain text name and address, device IDs) should be in raw, unhashed format. Hightouch normalizes these identifiers — for example, by lowercasing and removing spaces — to ensure they match data in the identity graph."
      }), "\n", _jsxs(_components.p, {
        children: ["For the rules that apply to each identifier, see ", _jsx(_components.a, {
          href: "/match-booster/configure-model#identifier-normalization",
          children: "Identifier normalization"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "how-should-i-model-multiple-identifiers-of-the-same-type",
        children: "How should I model multiple identifiers of the same type?"
      }), "\n", _jsx(_components.p, {
        children: "Consolidate all values for one identifier type in a single row. Capture multiple entries as an array. If you have just one value per identifier, a string is appropriate."
      }), "\n", _jsx(_components.p, {
        children: "For example:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "First Name"
            }), _jsx(_components.th, {
              children: "Email"
            }), _jsx(_components.th, {
              children: "Phone"
            }), _jsx(_components.th, {
              children: "uuid"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Tyler"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "\"tyler@work.com\""
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "[\"555-123-4567\" , \"999-555-1234\"]"
              })
            }), _jsx(_components.td, {
              children: "1"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Amina"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "[\"amina@work.com\", \"amina@gmail.com\"]"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "\"555-567-8309\""
              })
            }), _jsx(_components.td, {
              children: "2"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch formats the data to match the requirements of ad platform APIs."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "when-does-enrichment-take-place-does-hightouch-store-a-copy-of-my-data",
        children: "When does enrichment take place? Does Hightouch store a copy of my data?"
      }), "\n", _jsx(_components.p, {
        children: "After you configure Match Booster on a model, Hightouch runs a comparison of your selected identifiers against the identity graph and looks for exact matches. Hightouch uses a hashed version of your model's primary key to track matched rows."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch never stores your proprietary data. See ", _jsx(_components.a, {
          href: "/match-booster/overview#data-storage-and-privacy",
          children: "Data storage and privacy"
        }), " for details on how data flows."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "how-long-does-model-enrichment-take",
        children: "How long does model enrichment take?"
      }), "\n", _jsx(_components.p, {
        children: "After configuring your model and selecting which identifiers to use, you can expect enrichment to take a few hours and up to one day. This applies to both the initial enrichment and subsequent runs."
      }), "\n", _jsx(_components.p, {
        children: "The initial calibration (initialization) can take up to 72 hours."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "how-often-do-enriched-models-refresh",
        children: "How often do enriched models refresh?"
      }), "\n", _jsx(_components.p, {
        children: "You choose how frequently your models are refreshed using the enrichment schedule. Schedules are capped at 28 days to meet data retention requirements."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "how-can-i-check-when-my-model-was-last-enriched",
        children: "How can I check when my model was last enriched?"
      }), "\n", _jsxs(_components.p, {
        children: ["You can find enrichment status on the model's ", _jsx(_components.strong, {
          children: "Match Booster"
        }), " tab, including:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Enrichment schedule"
        }), "\n", _jsx(_components.li, {
          children: "Last enrichment time"
        }), "\n", _jsx(_components.li, {
          children: "Next scheduled enrichment"
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "can-i-manually-trigger-an-enrichment",
        children: "Can I manually trigger an enrichment?"
      }), "\n", _jsxs(_components.p, {
        children: ["Yes. Select ", _jsx(_components.strong, {
          children: "Initialize Match Booster"
        }), " (for first-time runs) or save your configuration to trigger enrichment from the model's ", _jsx(_components.strong, {
          children: "Match Booster"
        }), " tab."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "why-do-my-match-rates-appear-lower-with-match-booster",
        children: "Why do my match rates appear lower with Match Booster?"
      }), "\n", _jsx(_components.p, {
        children: "After the initial sync to an advertising platform, match rates can take up to 48 hours to stabilize depending on the platform. Allow enough time for results to calculate before comparing boosted versus non-boosted performance."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "why-is-snapchat-reporting-greater-than-100-match",
        children: "Why is Snapchat reporting greater than 100% match?"
      }), "\n", _jsx(_components.p, {
        children: "Snapchat reports match rates differently than other platforms. If you send multiple identifiers for the same user, Snapchat initially reports separate matches, which can result in a match rate greater than 100%. Snapchat deduplicates and resolves multiple matches to a single user over time. Check back in a day or two to see the stabilized match rate."
      })]
    });
  }
}
export default MDXContent;
